import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import viewerPresenceService from '../../../services/viewerPresenceService';
import './ActiveViewersBadge.css';

// Shows who else is currently viewing a project (heartbeat + overlay-click
// tracked server-side). Renders nothing while no one else is active.
// `variant` picks the layout: "thumbnail" absolutely positions the badge over
// a project card image; "inline" sits statically in a flex row (e.g. header
// meta info).
const ActiveViewersBadge = ({ projectId, variant = 'thumbnail' }) => {
  const [activeViewers, setActiveViewers] = useState([]);
  const [showViewersTooltip, setShowViewersTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const viewersIconRef = useRef(null);
  const tooltipRef = useRef(null);

  // Poll for active viewers every 45 seconds
  useEffect(() => {
    if (!projectId) {
      return;
    }

    let cancelled = false;

    // Fetch after a small deterministic stagger so a page with many badges
    // does not burst one viewer request per badge at the same instant.
    const fetchViewers = async () => {
      if (cancelled || document.hidden) {
        return;
      }
      try {
        const viewers = await viewerPresenceService.getActiveViewers(projectId);
        if (!cancelled) {
          setActiveViewers(viewers || []);
        }
      } catch (error) {
        console.warn(`❌ ActiveViewersBadge: Failed to fetch active viewers for project ${projectId}:`, error);
        if (!cancelled) {
          setActiveViewers([]);
        }
      }
    };

    const initialDelay = (Number(projectId) % 30) * 100;
    const initialTimeout = setTimeout(fetchViewers, initialDelay);
    const pollInterval = setInterval(fetchViewers, 45000);

    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      clearInterval(pollInterval);
    };
  }, [projectId]);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showViewersTooltip) {
      return;
    }
    const handleClickOutside = (event) => {
      if (viewersIconRef.current && !viewersIconRef.current.contains(event.target) &&
          tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowViewersTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showViewersTooltip]);

  if (activeViewers.length === 0) {
    return null;
  }

  const updateTooltipPosition = () => {
    if (viewersIconRef.current) {
      const rect = viewersIconRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX
      });
    }
  };

  return (
    <div
      className={`active-viewers-icon-container ${variant === 'thumbnail' ? 'thumbnail-position' : 'inline-position'}`}
      ref={viewersIconRef}
      onMouseEnter={() => {
        updateTooltipPosition();
        setShowViewersTooltip(true);
      }}
      onMouseLeave={() => {
        // Delay to allow mouse to move to tooltip
        setTimeout(() => {
          if (tooltipRef.current && !tooltipRef.current.matches(':hover')) {
            setShowViewersTooltip(false);
          }
        }, 100);
      }}
    >
      <button
        className="active-viewers-icon"
        title={`${activeViewers.length} user${activeViewers.length > 1 ? 's' : ''} viewing`}
        onClick={(e) => {
          e.stopPropagation();
          updateTooltipPosition();
          setShowViewersTooltip((prev) => !prev);
        }}
      >
        <i className="fas fa-user"></i>
        <span className="viewers-count">{activeViewers.length}</span>
      </button>

      {/* Tooltip showing all viewer emails */}
      {showViewersTooltip && typeof document !== 'undefined' && createPortal(
        <div
          className="viewers-tooltip"
          ref={tooltipRef}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`
          }}
          onMouseEnter={() => setShowViewersTooltip(true)}
          onMouseLeave={() => setShowViewersTooltip(false)}
        >
          <div className="viewers-tooltip-header">
            <i className="fas fa-users"></i>
            <span>{activeViewers.length} Active Viewer{activeViewers.length > 1 ? 's' : ''}</span>
          </div>
          <div className="viewers-tooltip-list">
            {activeViewers.map((email, index) => (
              <div key={index} className="viewers-tooltip-item">
                <i className="fas fa-user-circle"></i>
                <span>{email}</span>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActiveViewersBadge;
